// features/system/dbops/components/DbopsPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Admin → DB Ops panel.
//
// Zero-prop dashboard. Owns its data via useDbops().
//
// What it renders:
//   • Header strip: schema head (version + dirty bit) + data-freshness badge
//     + manual refresh button.
//   • Summary chips: total seeds, breakdown by env, oldest/newest apply time.
//   • Seed ledger table: file, env, sha256 (truncated, copyable), applied
//     timestamp, applied-by, freshness color dot.
//
// Skeleton-first: shows shimmer rows on initial load so perceived latency
// stays low while the orchestrator round-trips.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { useDbops, DbopsSeed } from '../hooks/useDbops';
import FreshnessIndicator from '@/shared/components/ui/FreshnessIndicator';

export default function DbopsPanel() {
  const { data, error, loading, refreshing, refresh, freshness, lastFetchedAt } = useDbops();

  return (
    <section className="pac-glass rounded-lg p-4 space-y-4">
      <Header
        freshness={freshness}
        refreshing={refreshing}
        onRefresh={refresh}
        lastFetchedAt={lastFetchedAt}
        schemaVersion={data?.schemaVersion ?? null}
        schemaDirty={data?.schemaDirty ?? null}
      />

      {error && (
        <div className="pac-glass-subtle rounded p-3 text-sm text-rose-300">
          <strong className="text-rose-200">Couldn't load dbops state.</strong>{' '}
          <span className="text-rose-300/80">{error}</span>
        </div>
      )}

      <Summary
        loading={loading}
        total={data?.summary.total ?? 0}
        byEnv={data?.summary.byEnv ?? {}}
        oldest={data?.summary.oldestAppliedAt ?? null}
        newest={data?.summary.newestAppliedAt ?? null}
      />

      <LedgerTable loading={loading} seeds={data?.seeds ?? []} />
    </section>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────
function Header(props: {
  freshness:     'live' | 'cached' | 'stale' | 'offline';
  refreshing:    boolean;
  onRefresh:     () => void;
  lastFetchedAt: number | null;
  schemaVersion: number | null;
  schemaDirty:   boolean | null;
}) {
  const dirtyBadge = props.schemaDirty
    ? <span className="ml-2 px-2 py-0.5 rounded text-xs bg-rose-900/40 text-rose-200 border border-rose-700/50">DIRTY</span>
    : props.schemaDirty === false
      ? <span className="ml-2 px-2 py-0.5 rounded text-xs bg-emerald-900/40 text-emerald-200 border border-emerald-700/50">CLEAN</span>
      : null;

  return (
    <header className="flex items-center justify-between gap-3 flex-wrap">
      <div className="space-y-0.5">
        <h2 className="text-lg font-semibold tracking-tight text-neutral-100">
          DB Ops
        </h2>
        <div className="text-sm text-neutral-400">
          Schema head:{' '}
          <span className="text-neutral-200 font-mono">
            {props.schemaVersion ?? '—'}
          </span>
          {dirtyBadge}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FreshnessIndicator
          lastFetchedAt={props.lastFetchedAt}
          isFetching={props.refreshing}
          label={`${props.freshness.toUpperCase()} · ${props.lastFetchedAt ? humanAge(Date.now() - props.lastFetchedAt) : '—'}`}
        />
        <button
          type="button"
          onClick={props.onRefresh}
          disabled={props.refreshing}
          className="pac-glass-btn text-sm px-3 py-1 rounded text-neutral-200 disabled:opacity-50"
        >
          {props.refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </header>
  );
}

// ─── Summary chips ──────────────────────────────────────────────────────
function Summary(props: {
  loading: boolean;
  total:   number;
  byEnv:   Record<string, number>;
  oldest:  string | null;
  newest:  string | null;
}) {
  if (props.loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[0,1,2,3].map(i => <SkeletonChip key={i} />)}
      </div>
    );
  }
  const envEntries = Object.entries(props.byEnv);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <Chip label="Total seeds"        value={String(props.total)} />
      <Chip label="Environments"       value={envEntries.length === 0 ? '—' : envEntries.map(([k,v]) => `${k}:${v}`).join(' · ')} />
      <Chip label="Oldest apply"       value={props.oldest ? humanAge(Date.now() - Date.parse(props.oldest)) + ' ago' : '—'} />
      <Chip label="Newest apply"       value={props.newest ? humanAge(Date.now() - Date.parse(props.newest)) + ' ago' : '—'} />
    </div>
  );
}

function Chip(props: { label: string; value: string }) {
  return (
    <div className="pac-glass-subtle rounded p-2">
      <div className="text-[10px] uppercase tracking-wide text-neutral-500">{props.label}</div>
      <div className="text-sm text-neutral-200 font-mono break-words">{props.value}</div>
    </div>
  );
}

function SkeletonChip() {
  return (
    <div className="pac-glass-subtle rounded p-2 animate-pulse">
      <div className="h-2 w-16 bg-neutral-700/50 rounded mb-2" />
      <div className="h-3 w-24 bg-neutral-700/40 rounded" />
    </div>
  );
}

// ─── Ledger table ────────────────────────────────────────────────────────
function LedgerTable(props: { loading: boolean; seeds: DbopsSeed[] }) {
  if (props.loading) {
    return (
      <div className="space-y-1">
        {[0,1,2,3,4].map(i => (
          <div key={i} className="pac-glass-subtle h-8 rounded animate-pulse" />
        ))}
      </div>
    );
  }
  if (props.seeds.length === 0) {
    return (
      <div className="pac-glass-subtle rounded p-3 text-sm text-neutral-400">
        No seed applies recorded. The dbops seeder Cloud Run Job hasn't run
        successfully yet for this database.
      </div>
    );
  }
  return (
    <div className="overflow-auto pac-glass-subtle rounded">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-neutral-500">
          <tr>
            <th className="p-2">Seed file</th>
            <th className="p-2">Env</th>
            <th className="p-2 hidden md:table-cell">SHA-256</th>
            <th className="p-2">Applied</th>
            <th className="p-2 hidden md:table-cell">By</th>
          </tr>
        </thead>
        <tbody>
          {props.seeds.map(s => (
            <tr key={`${s.seedFile}|${s.seedEnv}`} className="border-t border-neutral-800/60">
              <td className="p-2 font-mono text-neutral-200">{s.seedFile}</td>
              <td className="p-2">
                <span className="px-1.5 py-0.5 rounded text-xs bg-neutral-800/60 text-neutral-300">
                  {s.seedEnv}
                </span>
              </td>
              <td className="p-2 font-mono text-neutral-500 hidden md:table-cell"
                  title={s.sha256}>
                {s.sha256.slice(0, 12)}…
              </td>
              <td className="p-2 text-neutral-300 whitespace-nowrap">
                <FreshDot kind={s.freshness} />
                {humanAge(Date.now() - Date.parse(s.appliedAt))} ago
              </td>
              <td className="p-2 text-neutral-500 hidden md:table-cell">{s.appliedBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FreshDot(props: { kind: DbopsSeed['freshness'] }) {
  const color: Record<DbopsSeed['freshness'], string> = {
    fresh:   'bg-emerald-400',
    recent:  'bg-sky-400',
    aged:    'bg-neutral-500',
    unknown: 'bg-amber-400',
  };
  return <span className={`inline-block w-2 h-2 rounded-full mr-2 ${color[props.kind]}`} aria-hidden />;
}

// ─── Utilities ───────────────────────────────────────────────────────────
function humanAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60)        return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60)        return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24)        return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
